import { useEffect, useRef, useState } from 'react';
import { X, Move, Plus } from 'lucide-react';

/**
 * Canvas visual de plantilla de artículo — grid interno con elementos
 * arrastrables y redimensionables.
 *
 * Cada elemento tiene un tipo (categoria, titulo, resumen, imagen, cuerpo,
 * autor, separador, espacio) y ocupa un rectángulo (columnaInicio, filaInicio,
 * columnasSpan, filasSpan) dentro del grid definido por props.
 *
 * No persiste solo: llama a onCambio(nuevosElementos) con el array actualizado.
 */
export default function PlantillaArticuloCanvas({ columnas, filas, elementos, onCambio }) {
  const canvasRef = useRef(null);
  const [dims, setDims] = useState({ ancho: 0, alto: 0, celdaAncho: 0, celdaAlto: 0 });

  // Dimensiones responsive (proporción ~1.42, tipo A4)
  useEffect(() => {
    const compute = () => {
      const el = canvasRef.current;
      if (!el) return;
      const anchoDisp = el.clientWidth;
      const altoDisp  = el.clientHeight;
      const ratio = 0.707; // ancho/alto (~A4)
      let ancho = anchoDisp, alto = anchoDisp / ratio;
      if (alto > altoDisp) { alto = altoDisp; ancho = altoDisp * ratio; }
      setDims({ ancho, alto, celdaAncho: ancho / columnas, celdaAlto: alto / filas });
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (canvasRef.current) ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, [columnas, filas]);

  // Buscar hueco libre
  const buscarHueco = (w, h) => {
    for (let f = 1; f <= filas - h + 1; f++) {
      for (let c = 1; c <= columnas - w + 1; c++) {
        const solapa = elementos.some(el =>
          !(c + w - 1 < el.columnaInicio ||
            c > el.columnaInicio + el.columnasSpan - 1 ||
            f + h - 1 < el.filaInicio ||
            f > el.filaInicio + el.filasSpan - 1)
        );
        if (!solapa) return { columna: c, fila: f };
      }
    }
    return null;
  };

  const agregarElemento = (tipo) => {
    // Tamaños defaults por tipo
    let w = 6, h = 2;
    if (tipo === 'imagen') { w = 6; h = 4; }
    else if (tipo === 'cuerpo') { w = 12; h = 4; }
    else if (tipo === 'titulo') { w = 12; h = 2; }
    else if (tipo === 'categoria' || tipo === 'autor' || tipo === 'separador') { w = 12; h = 1; }
    else if (tipo === 'resumen') { w = 12; h = 2; }
    else if (tipo === 'referencia') { w = 12; h = 1; }
    else if (tipo === 'espacio') { w = 6; h = 1; }

    const intentos = [[w, h], [Math.min(w, 6), Math.min(h, 2)], [4, 1], [2, 1], [1, 1]];
    let hueco = null, usa = null;
    for (const [nw, nh] of intentos) {
      hueco = buscarHueco(nw, nh);
      if (hueco) { usa = [nw, nh]; break; }
    }
    if (!hueco) { alert('No hay espacio libre en la grilla.'); return; }
    onCambio([...elementos, {
      _key: `local-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      tipo,
      columnaInicio: hueco.columna, filaInicio: hueco.fila,
      columnasSpan: usa[0], filasSpan: usa[1],
      orden: elementos.length + 1,
      estilo: null,
    }]);
  };

  const eliminarElemento = (key) => {
    onCambio(elementos.filter(el => el._key !== key));
  };

  // Drag & resize
  const [drag, setDrag] = useState(null);
  const iniciarMove = (e, el) => {
    e.stopPropagation();
    setDrag({ key: el._key, tipo: 'move', startX: e.clientX, startY: e.clientY,
              orig: { col: el.columnaInicio, fila: el.filaInicio, cs: el.columnasSpan, fs: el.filasSpan } });
  };
  const iniciarResize = (e, el) => {
    e.stopPropagation();
    setDrag({ key: el._key, tipo: 'resize', startX: e.clientX, startY: e.clientY,
              orig: { col: el.columnaInicio, fila: el.filaInicio, cs: el.columnasSpan, fs: el.filasSpan } });
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const dCol = Math.round(dx / dims.celdaAncho);
      const dFil = Math.round(dy / dims.celdaAlto);
      const nuevos = elementos.map(el => {
        if (el._key !== drag.key) return el;
        if (drag.tipo === 'move') {
          const c = Math.max(1, Math.min(columnas - drag.orig.cs + 1, drag.orig.col + dCol));
          const f = Math.max(1, Math.min(filas    - drag.orig.fs + 1, drag.orig.fila + dFil));
          return { ...el, columnaInicio: c, filaInicio: f };
        } else {
          const cs = Math.max(1, Math.min(columnas - drag.orig.col + 1, drag.orig.cs + dCol));
          const fs = Math.max(1, Math.min(filas    - drag.orig.fila + 1, drag.orig.fs + dFil));
          return { ...el, columnasSpan: cs, filasSpan: fs };
        }
      });
      onCambio(nuevos);
    };
    const onUp = () => setDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag, dims, columnas, filas]);

  return (
    <div style={{ display:'flex', gap:12, height:'100%' }}>
      {/* Paleta */}
      <div style={{
        width:150, display:'flex', flexDirection:'column', gap:6,
        background:'#fff', border:'1px solid #d1d5db', borderRadius:4, padding:8,
      }}>
        <div style={{ fontSize:11, color:'#666', fontWeight:600, marginBottom:4 }}>Añadir elemento</div>
        {tiposElemento.map(t => (
          <button key={t.tipo} onClick={() => agregarElemento(t.tipo)}
            style={{ ...btnPaleta, borderColor: t.color, color: t.color }}>
            <Plus size={11} /> {t.label}
          </button>
        ))}
      </div>

      {/* Canvas */}
      <div ref={canvasRef} style={{ flex:1, minWidth:0, display:'flex', alignItems:'center', justifyContent:'center', padding:4 }}>
        <div style={{
          position:'relative',
          width: dims.ancho || '100%',
          height: dims.alto || '100%',
          background:'#fff',
          boxShadow:'0 4px 24px rgba(0,0,0,0.12)',
          backgroundImage: `
            linear-gradient(to right, #f0f0f0 1px, transparent 1px),
            linear-gradient(to bottom, #f0f0f0 1px, transparent 1px)
          `,
          backgroundSize: `${dims.celdaAncho}px ${dims.celdaAlto}px`,
        }}>
          {elementos.map(el => (
            <ElementoVisual
              key={el._key}
              elemento={el}
              celdaAncho={dims.celdaAncho}
              celdaAlto={dims.celdaAlto}
              onMove={(e) => iniciarMove(e, el)}
              onResize={(e) => iniciarResize(e, el)}
              onEliminar={() => eliminarElemento(el._key)}
              onColumnas={(n) => onCambio(elementos.map(x =>
                x._key === el._key
                  ? { ...x, estilo: escribirColumnasCuerpo(x.estilo, n) }
                  : x
              ))}
            />
          ))}
          {elementos.length === 0 && (
            <div style={{
              position:'absolute', inset:0, display:'flex', alignItems:'center',
              justifyContent:'center', color:'#aaa', fontSize:13, fontStyle:'italic',
              pointerEvents:'none',
            }}>
              Añade elementos desde la paleta →
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const tiposElemento = [
  { tipo:'titulo',     label:'Título',    color:'#111' },
  { tipo:'categoria',  label:'Categoría', color:'#c9a227' },
  { tipo:'resumen',    label:'Resumen',   color:'#4a5555' },
  { tipo:'imagen',     label:'Imagen',    color:'#0369a1' },
  { tipo:'cuerpo',     label:'Cuerpo',    color:'#374151' },
  { tipo:'autor',      label:'Autor',     color:'#7c3aed' },
  { tipo:'referencia', label:'Continúa en pág.', color:'#c9a227' },
  { tipo:'separador',  label:'Separador', color:'#999' },
  { tipo:'espacio',    label:'Espacio',   color:'#aaa' },
];

// El backend guarda las columnas propias de un cuerpo dentro de `estilo`
// (jsonb), bajo la clave columnasCuerpo. Si no está, manda el valor de la
// plantilla. Así una misma plantilla puede tener el cuerpo ancho de abajo a 3
// columnas y el angosto de al lado de la foto a 1.
function leerColumnasCuerpo(estilo) {
  if (!estilo) return null;
  try {
    const o = typeof estilo === 'string' ? JSON.parse(estilo) : estilo;
    const n = Number(o?.columnasCuerpo);
    return Number.isInteger(n) && n >= 1 ? n : null;
  } catch { return null; }
}

function escribirColumnasCuerpo(estilo, columnas) {
  let o = {};
  if (estilo) {
    try { o = typeof estilo === 'string' ? JSON.parse(estilo) : { ...estilo }; }
    catch { o = {}; }
  }
  if (columnas === null) delete o.columnasCuerpo;
  else o.columnasCuerpo = columnas;
  return Object.keys(o).length ? JSON.stringify(o) : null;
}

function ElementoVisual({ elemento, celdaAncho, celdaAlto, onMove, onResize, onEliminar, onColumnas }) {
  const meta = tiposElemento.find(t => t.tipo === elemento.tipo) || { color:'#333', label: elemento.tipo };
  return (
    <div style={{
      position:'absolute',
      left: (elemento.columnaInicio - 1) * celdaAncho,
      top:  (elemento.filaInicio    - 1) * celdaAlto,
      width:  elemento.columnasSpan * celdaAncho,
      height: elemento.filasSpan    * celdaAlto,
      background: `${meta.color}12`,
      border: `2px solid ${meta.color}`,
      borderRadius:2, overflow:'hidden',
      display:'flex', flexDirection:'column',
      boxSizing:'border-box',
    }}>
      <div onMouseDown={onMove}
        style={{
          padding:'2px 6px', background: `${meta.color}25`,
          cursor:'grab', display:'flex', alignItems:'center', gap:4,
          fontSize:10, color: meta.color, fontWeight:600,
        }}>
        <Move size={9} />
        <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {meta.label}
        </span>
        <button onClick={(e) => { e.stopPropagation(); onEliminar(); }}
          style={{ background:'transparent', border:'none', cursor:'pointer', color:'#dc2626', padding:0 }}>
          <X size={11} />
        </button>
      </div>
      <div style={{
        flex:1, display:'flex', flexDirection:'column', gap:3,
        alignItems:'center', justifyContent:'center',
        color: meta.color, fontSize: elemento.filasSpan >= 2 ? 11 : 9,
        fontStyle:'italic', textAlign:'center', padding:2, opacity:0.7,
      }}>
        <span>{elemento.columnasSpan}×{elemento.filasSpan}</span>
        {elemento.tipo === 'cuerpo' && (
          <label onMouseDown={(e) => e.stopPropagation()}
            style={{ fontStyle:'normal', fontSize:10, display:'flex', alignItems:'center', gap:4 }}
            title="Columnas periodísticas de este cuerpo. Vacío = el valor de la plantilla.">
            Columnas:
            <select
              value={leerColumnasCuerpo(elemento.estilo) ?? ''}
              onChange={(e) => onColumnas(e.target.value === '' ? null : Number(e.target.value))}
              style={{ fontSize:10, padding:'0 2px' }}>
              <option value="">plantilla</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
            </select>
          </label>
        )}
      </div>
      <div onMouseDown={onResize}
        style={{
          position:'absolute', right:0, bottom:0, width:12, height:12,
          background: meta.color, cursor:'nwse-resize',
        }}
        title="Arrastra para redimensionar" />
    </div>
  );
}

const btnPaleta = {
  display:'flex', alignItems:'center', gap:5,
  padding:'5px 7px', fontSize:11, fontWeight:600,
  background:'#fff', border:'1.5px solid', borderRadius:3,
  cursor:'pointer',
};
